// /en/notary/$act — English mirror of /notary/$act
// Rich content (1,800+ words) for international clients needing Thai Notarial Services.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NOTARY_ACTS, getNotaryAct } from "@/data/notary-acts";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import NotaryTripleKeyLinks from "@/components/NotaryTripleKeyLinks";
import NotaryAuthorByline from "@/components/NotaryAuthorByline";
import RelatedNotaryLinks from "@/components/RelatedNotaryLinks";
import NotaryActDeepContent from "@/components/NotaryActDeepContent";
import {
  ScrollText, ShieldCheck, FileCheck2, Clock,
  MessageCircle, Phone, BadgeCheck, Globe2, Building2, MapPin,
} from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

// Expanded English use-case copy — fills the 1,800-word target.
// Keyed by slug; falls back to act.useCases (joined to English narrative).
const EN_USES: Record<string, string[]> = {
  "certify-signature": [
    "Signing foreign contracts (US, UK, EU, AU)",
    "Bank KYC and account opening abroad",
    "Verifying your identity for overseas authorities",
    "Corporate filings with foreign registries (BVI, Cayman, Singapore)",
  ],
  "power-of-attorney": [
    "Authorising a lawyer to sell property in your home country",
    "Granting banking authority abroad",
    "Representing you in foreign litigation",
    "Closing foreign brokerage or pension accounts",
  ],
  "affidavit-single": [
    "Marrying abroad — required by most civil registries",
    "K-1 Fiancé Visa (US)",
    "Spouse / partner visas (UK, AU, EU)",
  ],
  "dbd-signature": [
    "Filing DBD director change with the Ministry of Commerce",
    "Updating shareholders, directors, registered address",
    "Capital increase / decrease registration",
  ],
  "life-certificate": [
    "Claiming overseas pension (US Social Security, Singapore CPF, EU pensions)",
    "Maintaining senior-citizen benefits abroad",
    "Annual proof-of-life for foreign banks and insurers",
  ],
};

const EN_INTRO: Record<string, string> = {
  "certify-signature":
    "Signature certification — also called signature acknowledgement — is the most-requested notarial act in Thailand. A Notarial Services Attorney verifies your identity and witnesses you signing the document. The notary affixes their seal and a certificate confirming the signature is genuine. This is the act foreign authorities, banks, and registries expect when they ask for a 'notarised' Thai document.",
  "certified-true-copy":
    "A Certified True Copy is a photocopy of an original document that a Thai Notarial Services Attorney has compared to the original and certified as a true and complete reproduction. It is the standard way to send a Thai passport, ID card, house registration, or academic transcript abroad without parting with the original.",
  "power-of-attorney":
    "A notarised Power of Attorney lets you appoint another person — your lawyer, family member, or business partner — to act on your behalf in a specific transaction abroad. The notarial attorney verifies your identity, confirms your understanding, and certifies your signature so the foreign authority accepts the document at face value.",
  "dbd-signature":
    "DBD Director Signature Certification is a specialised Thai notarial act required when a director files changes to a registered company at the Department of Business Development (กรมพัฒนาธุรกิจการค้า). The notary verifies the director in person and certifies the signature on the DBD form, which the Ministry of Commerce then accepts as proof of the director's authority.",
  "life-certificate":
    "A Proof of Life (Life Certificate) is required by many foreign pension authorities — US Social Security, Singapore CPF, German Deutsche Rentenversicherung, UK DWP, and others — to confirm that a beneficiary living in Thailand is still alive. A Thai Notarial Services Attorney verifies the person in front of them and issues the certificate the foreign authority accepts.",
};

export const Route = createFileRoute("/en/notary/$act/")({
  loader: ({ params }) => {
    if (!getNotaryAct(params.act)) throw notFound();
    return { slug: params.act };
  },
  head: ({ params }) => {
    const n = getNotaryAct(params.act);
    if (!n) return { meta: [{ title: ctaTitle("Service not found", "/en/notary/$act", "en")}] };
    const url = `${SITE}/en/notary/${n.slug}`;
    const title = `${n.nameEn} in Bangkok — Notary Public Thailand | From ฿${n.priceFromTh.toLocaleString()}`;
    const desc = `${n.nameEn} (${n.nameTh}) by a court-licensed Notarial Services Attorney in Bangkok. Same-day available. Embassy-accepted. Used by Fortune-500 HR, law firms, and 2,000+ expats. Send your documents for a free check.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/notary/$act", "en") }, { name: "description", content: ctaDesc(desc, "/en/notary/$act", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/notary/$act", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/notary/$act", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
        { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/notary/${n.slug}`, `/en/notary/${n.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: n.nameEn,
              alternateName: n.nameTh,
              serviceType: "Notary Public",
              inLanguage: "en",
              areaServed: { "@type": "Country", name: "Thailand" },
              provider: {
                "@type": "LegalService", "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service", url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer", price: n.priceFromTh, priceCurrency: "THB",
                availability: "https://schema.org/InStock",
              },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo", "@id": `${url}#howto`,
              name: `How to get ${n.nameEn} in Bangkok`,
              inLanguage: "en",
              totalTime: "P1D",
              estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: String(n.priceFromTh) },
              step: [
                { "@type": "HowToStep", position: 1, name: "Send a scan via LINE", text: "Message @nycli with a clear scan or photo of the document. We confirm the act, fee, and signing slot within 30 minutes." },
                { "@type": "HowToStep", position: 2, name: "Book the signing slot", text: "Pick an in-office slot at our Lat Phrao 95 office (Mon–Sat 09:00–18:00) or request the attorney to travel to you within Bangkok." },
                { "@type": "HowToStep", position: 3, name: "Sign before the attorney", text: "Bring your passport. The Notarial Services Attorney verifies your identity, witnesses your signature, and affixes the notarial seal." },
                { "@type": "HowToStep", position: 4, name: "Collect or receive by courier", text: "Pick up the notarised document the same day or have it delivered by EMS / Grab anywhere in Bangkok. Add MFA Apostille if going abroad." },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Notary Public", item: `${SITE}/en/notary` },
                { "@type": "ListItem", position: 3, name: n.nameEn, item: url },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage", "@id": `${url}#faq`,
              mainEntity: enFaqs(n).map((f) => ({
                "@type": "Question", name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({
            type: "application/ld+json" as const,
            children: JSON.stringify(r),
          })),
        ]),
      ],
    };
  },
  component: EnNotaryActPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Service not found</h1>
      <Link to="/en/notary" className="mt-4 inline-block text-gold underline">Back to Notary hub</Link>
    </main>
  ),
});

function enFaqs(n: ReturnType<typeof getNotaryAct> & object) {
  const safe = n!;
  return [
    {
      q: `Who can perform ${safe.nameEn} in Thailand?`,
      a: `Only a Notarial Services Attorney — a Thai lawyer licensed by the Lawyers Council of Thailand to act as a notary public — may perform ${safe.nameEn}. Thailand does not have a separate notary profession; the function is delegated to specially licensed attorneys. NYC Legal & Notary Service has six such attorneys on staff with over 100 years of combined practice.`,
    },
    {
      q: `How long does ${safe.nameEn} take?`,
      a: `${safe.turnaround} for the standard process. Same-day service is available for an additional ฿1,000 if you send the document before 11:00 and bring valid ID to the signing. Walk-ins are accepted Monday to Saturday, 09:00–18:00.`,
    },
    {
      q: `What is the price for ${safe.nameEn}?`,
      a: `${safe.nameEn} starts at ฿${safe.priceFromTh.toLocaleString()} for a single document. Add ฿1,500 for the attorney to travel to your office or hotel within Bangkok. Add ฿2,500 for downstream MFA Apostille (Ministry of Foreign Affairs) so the document is valid in Hague Convention countries.`,
    },
    {
      q: `Will my home country accept a Thai notarisation?`,
      a: `Yes. Notarisations by a Thai Notarial Services Attorney are recognised by US embassies, UK Home Office, Australian DHA, Singapore ICA, EU consulates, BVI/Cayman corporate registries, and Fortune-500 HR departments worldwide. For full international recognition, add the MFA Apostille (or embassy legalization for non-Hague countries) — we handle the entire chain.`,
    },
    {
      q: `Do I need to attend in person?`,
      a: `Yes. Thai notarial law requires the signer to appear in person before the attorney. Remote / online notarisation is not legally recognised in Thailand. If you cannot travel to our Lat Phrao 95 office, the attorney can travel to your Bangkok address for an additional ฿1,500.`,
    },
    {
      q: `What documents must I bring?`,
      a: `Always bring your original passport (or Thai national ID for Thai signers). For ${safe.nameEn}, you also need: ${safe.required.join("; ")}. If your draft is in English we will review it before signing; if it is in another language, please bring a certified translation.`,
    },
  ];
}

function EnNotaryActPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getNotaryAct(slug)!;
  const related = NOTARY_ACTS.filter((x) => x.slug !== slug && x.category === n.category).slice(0, 6);
  const uses = EN_USES[slug] ?? n.useCases;
  const intro = EN_INTRO[slug] ??
    `${n.nameEn} (${n.nameTh}) is a notarial act performed in Thailand by a Notarial Services Attorney — a lawyer licensed by the Lawyers Council of Thailand to act as a notary public. The attorney verifies your identity, witnesses your signature where applicable, and affixes a notarial seal so foreign authorities accept the document at face value.`;
  const faqs = enFaqs(n);

  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/notary" className="hover:text-gold">Notary</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{n.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ScrollText className="h-3.5 w-3.5" /> Thai Notary Public · {n.category.toUpperCase()}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {n.nameEn}
          </h1>
          <p className="mt-2 text-base text-white/80">{n.nameTh}</p>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{intro}</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "From", v: `฿${n.priceFromTh.toLocaleString()}` },
              { k: "Turnaround", v: n.turnaround },
              { k: "Walk-in", v: "Mon–Sat 09–18" },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition min-h-[44px] focus-visible:ring-2 focus-visible:ring-white/60">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition min-h-[44px] focus-visible:ring-2 focus-visible:ring-white/60">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
            <Link to="/en/quote" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-2.5 text-sm font-bold text-white hover:bg-white/10 min-h-[44px] focus-visible:ring-2 focus-visible:ring-white/60">
              Get a written quote
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        {/* Quotable AEO block */}
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC Legal &amp; Notary Service (DBD 0435567000061) provides {n.nameEn} ({n.nameTh}) in Bangkok, Thailand, performed by a court-licensed Notarial Services Attorney registered with the Lawyers Council of Thailand. Fees start at ฿{n.priceFromTh.toLocaleString()} with {n.turnaround} turnaround. Rated 4.9/5 by 2,147 international clients.
        </blockquote>

        {/* Use cases */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">When you need {n.nameEn}</h2>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
            {n.nameEn} is required whenever a foreign authority, bank, university, registry, or court needs evidence that the document — or your signature on it — has been verified by an independent legal officer in Thailand. Typical scenarios:
          </p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {uses.map((u) => (
              <li key={u} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
              </li>
            ))}
          </ul>
        </section>

        {/* What to bring */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">What to bring</h2>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
            Thai notarial law requires the signer to appear in person before the attorney with original identification. For {n.nameEn} you should bring:
          </p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {n.required.map((r) => (
              <li key={r} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 text-sm text-slate-800">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {r}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[14px] leading-relaxed text-slate-600">
            If you are unsure whether your document is ready for notarisation — for instance, the draft is missing a clause the foreign authority requires — send a scan to LINE @nycli and we will review it before you travel to the office. Reviews take fewer than 30 minutes during business hours.
          </p>
        </section>

        {/* How it works (HowTo mirror) */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">How {n.nameEn} works at NYC Legal</h2>
          <ol className="mt-5 space-y-4">
            {[
              { t: "1. Send the document via LINE", d: "Message @nycli with a clear scan or photo of the document and tell us the destination country. Our paralegal confirms the correct act, fee, and signing slot within 30 minutes during business hours." },
              { t: "2. Book your signing slot", d: "Choose an in-office slot at Lat Phrao 95, Bangkok (Mon–Sat 09:00–18:00) or request the Notarial Services Attorney to travel to your office, hotel, or hospital anywhere in Bangkok for an additional ฿1,500." },
              { t: "3. Sign before the attorney", d: "Bring your passport (and any documents listed above). The attorney verifies your identity, confirms you understand the document, witnesses your signature, and affixes the notarial seal and certificate." },
              { t: "4. Collect or courier", d: "Most notarisations are completed in one business day. Pick up the original at our office or have it delivered by EMS, Grab, or Lalamove anywhere in Thailand. Add MFA Apostille (฿2,500) if the document is going to a Hague Convention country." },
            ].map((s) => (
              <li key={s.t} className="rounded-2xl border border-slate-200 bg-white p-5">
                <p className="text-base font-bold text-navy-deep">{s.t}</p>
                <p className="mt-1 text-[14px] leading-relaxed text-slate-700">{s.d}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Pricing */}
        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Pricing</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {n.nameEn} from ฿{n.priceFromTh.toLocaleString()}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Standard turnaround: {n.turnaround}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Same-day +฿1,000 (document received before 11:00)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Building2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Attorney travels to you within Bangkok +฿1,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Add MFA Apostille +฿2,500 (includes government fee)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Add embassy legalization (non-Hague) +฿3,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Volume discount on 5+ documents</li>
          </ul>
        </section>

        {/* Trust */}
        <section className="mt-12 grid gap-5 sm:grid-cols-3">
          {[
            { t: "Court-licensed", d: "Six Notarial Services Attorneys on staff, all registered with the Lawyers Council of Thailand. 100+ years of combined practice." },
            { t: "Embassy-trusted", d: "Used daily by US, UK, AU, DE, JP embassies and consulates in Bangkok. Recognised by Fortune-500 HR teams and tier-1 law firms." },
            { t: "End-to-end", d: "Notarisation, MFA Apostille, embassy legalization, certified translation — handled in-house under one roof. Single point of contact." },
          ].map((c) => (
            <article key={c.t} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <p className="text-sm font-extrabold text-navy-deep">{c.t}</p>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-700">{c.d}</p>
            </article>
          ))}
        </section>

        {/* FAQ */}
        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently asked questions</h2>
          <div className="mt-5 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-2xl border border-slate-200 bg-white p-4 open:bg-slate-50/70">
                <summary className="cursor-pointer list-none text-[14px] font-bold text-navy-deep marker:hidden sm:text-[15px]">
                  {f.q}
                </summary>
                <p className="mt-2 text-[13px] leading-relaxed text-slate-700 sm:text-[14px]">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Related notarial acts</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/en/notary/$act" params={{ act: r.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px] focus-visible:ring-2 focus-visible:ring-amber-400">
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{r.nameEn}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">From ฿{r.priceFromTh.toLocaleString()} · {r.turnaround}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* TH counterpart */}
        <section className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
          <MapPin className="mb-2 inline h-4 w-4 text-amber-600" /> Thai speaker?{" "}
          <Link to="/notary/$act" params={{ act: slug }} className="font-bold text-amber-700 hover:underline">
            ดูหน้าภาษาไทย: {n.nameTh}
          </Link>
        </section>

        <NotaryTripleKeyLinks mode="en" actSlug={slug} actNameTh={n.nameTh} actNameEn={n.nameEn} />
      </div>

      <NotaryActDeepContent act={n} lang="en" />
      <NotaryAuthorByline lang="en" />
      <RelatedNotaryLinks context="act" lang="en" currentAct={slug} />
      <TrustLayer variant="compact" />
    </main>
  );
}
